import { useContext, useEffect, useMemo, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'

import { Form, Col, Row } from 'react-bootstrap'
import './style.css'
import DatePickerCustom from './DatePicker/DatePickerCustom'
import TimePickerInput from './TimePicker/TimePickerInput'
import moment from 'moment'
import objectPath from 'object-path'
import Label from './Label'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { createUischemaStyle } from './Style'
import { getCurrentUi } from '../action/Data'
import { INVALID_DATE_TIME_VALUE } from '../validation/dateTime'

const CustomDateTime = (props) => {
  let {
    value,
    uischema,
    updateData,
    copyData = () => {},
    errors = {},
    label,
    setIsOnChange,
    fieldName,
    fieldKey,
    config = {},
  } = props
  let {
    placeholder,
    maskSign,
    year,
    key,
    elementWidth,
    controlId,
    suffixText,
    require,
    formatType,
  } = uischema

  const {
    isDynamic = false,
    dynamicName = '',
    dynamicKey = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
    arrayKey,
  } = config

  let errorArray = objectPath.get(errors, key) || []

  const designContext = useContext(DesignContext)
  const {
    elementAction,
    jsonForm,
    setFieldFormData,
    setFieldSearchData,
    formatDateTime,
    isUiChanged,
    setIsUiChanged,
  } = designContext

  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  const uiStyle = useMemo(
    () => createUischemaStyle({ uischema, showRequire }),
    [uischema, showRequire]
  )

  const checkDateText = (date) => {
    if (!uischema?.dateText) return
    if (!formatDateTime) return
    let { formatSort, timeFormat, textField } = uischema?.dateText
    let textKey = textField.key
    let ui = getCurrentUi(textKey, jsonForm.schema, jsonForm.uischema)
    let uiKey = ui.key
    let uiName = ui.name
    let dateValue = date?.format
    let textValue
    if (dateValue) {
      let dateList = []
      if (formatSort) {
        formatSort.forEach((format) => {
          let formatValue = objectPath.get(uischema?.dateText, format)
          let value = formatDateTime(dateValue, formatValue)
          dateList.push(value)
        })
      }
      if (timeFormat) {
        let value = formatDateTime(dateValue, timeFormat)
        dateList.push(value)
      }
      textValue = dateList.join(' ')
    } else {
      textValue = ''
    }
    if (isDynamic) {
      objectPath.set(dynamicData, uiKey, textValue)
      setFieldFormData(`${dynamicName}.${dynamicIndex}.${uiName}`, textValue)
      onUpdateFormData(`${dynamicName}.${dynamicIndex}.${uiName}`, textValue)
    } else {
      setFieldFormData(uiKey, textValue)
      setFieldFormData(uiName, textValue)
      onUpdateFormData(uiName, textValue)
    }
    objectPath.set(isUiChanged, uiKey, { isRender: true })
    setIsUiChanged(isUiChanged)
  }

  let { dateFormat, timeFormat } = useTimeFormat()

  const isCustomFormat = formatType === 'custom'

  const formatDate = useMemo(() => {
    if (isCustomFormat) {
      return uischema.dateFormat
    }
    return dateFormat
  }, [formatType, uischema.dateFormat, uischema.year])

  const formatTime = useMemo(() => {
    if (isCustomFormat) {
      return uischema.timeFormat
    }
    return timeFormat
  }, [formatType, uischema.timeFormat, uischema.time])

  const [dateString, setDateString] = useState()
  const [timeString, setTimeString] = useState()

  useEffect(() => {
    if (value === INVALID_DATE_TIME_VALUE) return
    let dateString = value ? moment(value).format('YYYY-MM-DD') : null
    setDateString(dateString)
  }, [value])

  useEffect(() => {
    if (value === INVALID_DATE_TIME_VALUE) return
    let timeString = value ? moment(value).format('HH:mm:ss') : null
    setTimeString(timeString)
  }, [value])

  const updateDate = (date, isOnChange) => {
    if (date.value === INVALID_DATE_TIME_VALUE) {
      updateData(
        {
          format: INVALID_DATE_TIME_VALUE,
          value: INVALID_DATE_TIME_VALUE,
        },
        isOnChange,
      )
      return
    }
    let timeValue =
      timeString === 'Invalid date' ? moment().format('HH:mm:ss') : timeString
    let format = `${moment(date.value).format('YYYY-MM-DD')} ${timeValue}`
    let value = moment(date.value).format('YYYY-MM-DDTHH:mm:ssZ')
    setDateString(moment(value).format('YYYY-MM-DD'))
    updateData({ format, value }, isOnChange)
  }
  const updateTime = (time, isOnChange) => {
    if (time.value === INVALID_DATE_TIME_VALUE) {
      updateData(
        {
          format: INVALID_DATE_TIME_VALUE,
          value: INVALID_DATE_TIME_VALUE,
        },
        isOnChange,
      )
      return
    }
    let dateValue =
      dateString === 'Invalid date' ? moment().format('YYYY-MM-DD') : dateString

    let format = `${dateValue} ${time.value}`
    let value = moment(format).format('YYYY-MM-DDTHH:mm:ssZ')
    setTimeString(moment(value).format('HH:mm:ss'))
    updateData({ format, value }, isOnChange)
  }

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isRequire={isRequire}
          isEnable={!isReadOnly}
        />
        <Col sm={elementWidth} className="mt-1 p-0 d-flex designer-date-time-inputs">
          <DatePickerCustom
            id={fieldKey}
            name={fieldName}
            formatDisplay={formatDate}
            placeholder={placeholder}
            value={dateString}
            onChange={(date) => {
              if (date?.value) {
                updateDate(date, true)
              } else {
                updateData({ format: null, value: null }, true)
              }
              setIsOnChange(true)
              // checkDateText(date)
            }}
            style={{ background: isReadOnly ? '#e9ecef' : '', ...uiStyle }}
            isClearable={!isReadOnly}
            readonly={isReadOnly}
            maskSign={maskSign}
            calendar={year}
            validateInput
          />
          <div className={`d-flex designer-time-input ${isReadOnly ? 'pl-2' : 'pl-4'}`}>
            <TimePickerInput
              id={fieldKey}
              name={fieldName}
              formatDisplay={formatTime}
              style={{ background: isReadOnly ? '#e9ecef' : '', ...uiStyle }}
              ampm={uischema.time === '12' ? true : false}
              readOnly={isReadOnly}
              validateInput
              value={timeString}
              onChange={(e) => {
                let time = { value: e }
                if (e) {
                  updateTime(time, true)
                } else {
                  updateData({ format: null, value: null }, true)
                }
                setIsOnChange(true)
              }}
            />
          </div>
        </Col>
        {suffixText && (
          <Col className="d-flex align-items-center mt-1 pr-0" sm="auto">
            <Form.Label className={`m-0 w-100`}>{suffixText}</Form.Label>
          </Col>
        )}
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}
        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {errorArray.map((error, index) => (
          <li key={`error-${key}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

export default CustomDateTime
